import { useEffect } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Account, Address, Chain, Client, Transport } from "viem"
import { useAccount, useConnectorClient } from "wagmi"

import { selectBitcoinAddress, useDappSelector } from "#/redux-state"
import {
  BITCOIN_MIN_DEPOSIT,
  ETHEREUM_CHAIN_ID,
  queryKeysFactory,
} from "#/shared/constants"
import { tbtcApi } from "#/shared/services"
import { convertBitcoinToSatoshi } from "#/shared/utils"

import useSendBitcoin from "../bitcoin"
import { useGenerateTbtcDeposit } from "../tbtc"
import { clientToSignerEthersV5 } from "../wagmi"

import useBridgingHistory from "./useBridgingHistory"

export const BITCOIN_MIN_DEPOSIT_SATOSHI =
  convertBitcoinToSatoshi(BITCOIN_MIN_DEPOSIT)

export class InsufficientBitcoinDepositError extends Error {
  depositedAmount: bigint

  constructor(amount: bigint) {
    super("Insufficient Bitcoin deposit")
    this.depositedAmount = amount
  }
}

export default function useBridgeBitcoin(
  isBitcoinTxSendingFromAnotherWallet = false,
) {
  const { mutateAsync: generateTbtcDeposit, data: generateDepositData } =
    useGenerateTbtcDeposit()

  const { connector, address } = useAccount()
  const btcAddress = useDappSelector(selectBitcoinAddress)

  const { queryKey: connectorClientQueryKey } = useConnectorClient({
    chainId: ETHEREUM_CHAIN_ID,
    connector,
  })

  const queryClient = useQueryClient()

  const { mutateAsync: sendBitcoin, data: bitcoinTxHash } = useSendBitcoin()
  const { setLatestBridgeTransaction } = useBridgingHistory()

  const {
    mutateAsync: initializeBitcoinDeposit,
    isPending: isInitializingDeposit,
    isSuccess: isBitcoinDepositCreated,
    error: initializeBitcoinDepositError,
    reset: resetInitializeBitcoinDeposit,
  } = useMutation({
    mutationFn: async ({
      recipient,
      amount,
      bitcoinRecoveryAddress,
    }: {
      recipient?: Address
      amount: bigint | undefined
      bitcoinRecoveryAddress: string | null | undefined
    }) => {
      if (!bitcoinRecoveryAddress || !amount || !connector || !address) {
        throw new Error("Invalid data")
      }

      const client = await queryClient.fetchQuery<
        Client<Transport, Chain, Account>
      >({
        queryKey: connectorClientQueryKey,
      })

      let deposit = generateDepositData
      if (!deposit) {
        const ethersSigner = await clientToSignerEthersV5(client, connector)

        deposit = await generateTbtcDeposit({
          recipient: recipient ?? address,
          bitcoinRecoveryAddress,
          ethersSigner,
          loggedInUser: btcAddress ?? address,
        })
      }

      const { bitcoinDepositAddress } = deposit
      if (isBitcoinTxSendingFromAnotherWallet) return

      await sendBitcoin({ recipient: bitcoinDepositAddress, amount })
    },
  })

  const {
    error: waitForBitcoinDepositTxError,
    isFetching: isWaitingForBitcoinDepositTx,
    isSuccess: isBitcoinTxConfirmed,
    refetch: retryWaitForBitcoinDepositTx,
  } = useQuery({
    queryKey: [
      ...queryKeysFactory.userKeys.waitForBitcoinDepositTransaction(),
      generateDepositData?.bitcoinDepositAddress,
    ],
    queryFn: async () => {
      if (!generateDepositData) throw new Error("Generate deposit first")

      const { deposit } = generateDepositData
      const utxos = await deposit?.detectFunding()

      if (utxos.length === 0) {
        throw new Error("Deposit not funded yet")
      }

      const utxo = utxos[0]
      const utxoValue = BigInt(utxo.value.toString())

      if (utxoValue < BITCOIN_MIN_DEPOSIT_SATOSHI) {
        throw new InsufficientBitcoinDepositError(utxoValue)
      }

      setLatestBridgeTransaction(
        null,
        "BTC",
        8,
        utxo.transactionHash.toString(),
      )

      return utxo
    },
    retry: (failureCount, failureReason) => {
      if (
        // If this is `InsufficientBitcoinDepositError` error we do not want
        // to retry.
        failureReason instanceof InsufficientBitcoinDepositError ||
        // Max retries 10
        failureCount === 10
      )
        return false

      return true
    },
    enabled:
      !!generateDepositData &&
      (!!bitcoinTxHash || isBitcoinTxSendingFromAnotherWallet),
    refetchOnWindowFocus: false,
  })

  const {
    data: createdDepositData,
    mutateAsync: createDeposit,
    isPending: isCreatingDeposit,
    isSuccess: isDepositCreatedSuccessfully,
    error: createDepositError,
    reset: resetCreateDeposit,
  } = useMutation({
    mutationFn: async () => {
      if (!generateDepositData) throw new Error("Generate deposit first")

      if (!address) throw new Error("Missing recipient")

      const generatedDepositData = generateDepositData.deposit.getReceipt()

      const depositReceipt = {
        depositor: generatedDepositData.depositor.identifierHex,
        blindingFactor: generatedDepositData.blindingFactor.toString(),
        walletPublicKeyHash:
          generatedDepositData.walletPublicKeyHash.toString(),
        refundPublicKeyHash:
          generatedDepositData.refundPublicKeyHash.toString(),
        refundLocktime: generatedDepositData.refundLocktime.toString(),
        extraData: generatedDepositData.extraData!.toString(),
      }

      return tbtcApi.createDeposit({
        depositReceipt,
        recipient: address,
      })
    },
  })

  useEffect(() => {
    if (!isBitcoinTxConfirmed) return

    createDeposit()
  }, [isBitcoinTxConfirmed, createDeposit])

  const {
    isSuccess: isCreatedDepositConfirmed,
    isFetching: isConfirmingCreatedDeposit,
    error: confirmDepositCreatedError,
    refetch: retryConfirmCreatedDeposit,
  } = useQuery({
    queryKey: [
      ...queryKeysFactory.userKeys.confirmCreatedDeposit(),
      createdDepositData?.depositId,
    ],
    queryFn: async () => {
      const { depositId: createdDepositId } = createdDepositData!

      const depositById = await tbtcApi.getDepositById(createdDepositId)

      if (depositById.depositId !== createdDepositId)
        throw new Error(
          "Failed to confirm that the deposit was created successfully",
        )

      return depositById
    },
    enabled: !!createdDepositData,
  })

  const isPending =
    isInitializingDeposit ||
    isWaitingForBitcoinDepositTx ||
    isCreatingDeposit ||
    isConfirmingCreatedDeposit

  return {
    // Generate deposit.
    depositAddress: generateDepositData?.bitcoinDepositAddress,
    revealData: generateDepositData?.revealData,
    initializeBitcoinDeposit,
    isInitializingDeposit,
    initializeBitcoinDepositError,
    resetInitializeBitcoinDeposit,
    isBitcoinDepositCreated,

    // Wait for deposit transaction.
    isWaitingForBitcoinDepositTx,
    waitForBitcoinDepositTxError,
    retryWaitForBitcoinDepositTx,
    isBitcoinTxConfirmed,

    // Create deposit.
    createDeposit,
    resetCreateDeposit,
    isDepositCreatedSuccessfully,
    isCreatingDeposit,
    createDepositError,

    // Created deposit confirmation.
    isCreatedDepositConfirmed,
    isConfirmingCreatedDeposit,
    confirmDepositCreatedError,
    retryConfirmCreatedDeposit,

    // General loading indicator.
    isPending,
  }
}
